import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { styled } from "@linaria/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { ArrowCounterClockwiseIcon, CaretDownIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import RotateWithTransition from "../animations/RotateWithTransition";
import FlexItem from "../containers/FlexItem";
import FlexWrapper, { AlignItems, FlexDirection } from "../containers/FlexWrapper";
import HorizontalDivider from "../dividers/HorizontalDivider";
import Dropdown, { DropdownPosition, DropdownVariant } from "../dropdown/Dropdown";
import Icon, { IconVariant } from "../icons/Icon";
import Text, { TextSize, TextVariant } from "../text/Text";
import CheckboxInput, { CheckboxInputVariant } from "./CheckboxInput";
import { useControlledOpenState, useDebouncedValue, useElementWidth } from "./hooks";
import { InputLabel } from "./Input";
import {
  SELECT_INPUT_SIZE_TO_ICON_SIZE,
  SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP,
  SELECT_INPUT_VARIANT_TO_ICON_VARIANT_MAP,
  SelectInputSize,
  SelectInputVariant,
  type SelectInputOption,
} from "./SelectInput";

export interface PinnedOptions {
  id: string;
  label: string;
  optionIds: string[];
}

export interface MultiSelectInputProps {
  label?: string;
  icon?: PhosphorIcon;
  variant?: SelectInputVariant;
  size?: SelectInputSize;
  options: SelectInputOption[];
  value: SelectInputOption[];
  placeholder?: string;
  width?: number | string;
  fillWidth?: boolean;
  dropdownWidth?: number | string;
  dropdownPlacement?: DropdownPosition;
  onChange?: (value: SelectInputOption[]) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  isOpen?: boolean;
  isIconTrailing?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onReset?: () => void;
  pinnedOptions?: PinnedOptions[];
  maxDisplayItems?: number;
  renderSelectedText?: (selectedOptions: SelectInputOption[], placeholder: string) => string;
  renderSelectedContent?: (selectedOptions: SelectInputOption[], placeholder: string) => ReactNode;
  error?: string;
  onSearch?: (searchTerm: string, options: SelectInputOption[]) => SelectInputOption[];
  debounceMs?: number;
}

const StyledMultiSelectInput = withTheme(styled.div<
  PropsWithTheme<{
    $variant: SelectInputVariant;
    $size: SelectInputSize;
    $isActive: boolean;
    $isDisabled: boolean;
    $isError: boolean;
    $width?: number | string;
    $fillWidth?: boolean;
  }>
>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: ${({ $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => "0 6px")
      .with(SelectInputSize.MEDIUM, () => "0 8px")
      .with(SelectInputSize.LARGE, () => "0 10px")
      .exhaustive();
  }};

  height: ${({ $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => "24px")
      .with(SelectInputSize.MEDIUM, () => "28px")
      .with(SelectInputSize.LARGE, () => "32px")
      .exhaustive();
  }};
  width: ${({ $fillWidth, $width }) =>
    $fillWidth ? "100%" : $width ? getCssSizeValue($width) : "auto"};

  cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};

  background: ${({ theme }) => theme.color.background.primary};
  border: 0.5px solid
    ${({ theme, $isActive, $isError }) => {
      if ($isActive) {
        return theme.color.border.selected;
      }

      if ($isError) {
        return theme.color.border.error;
      }

      return theme.color.border.primary;
    }};
  border-radius: 4px;

  transition: border-color 0.075s ease-in-out;

  &:hover {
    border: 0.5px solid
      ${({ theme, $isActive }) =>
        $isActive ? theme.color.border.selected : theme.color.border.primary};
  }
`);

const StyledSearchInput = withTheme(styled.input<
  PropsWithTheme<{
    $variant: SelectInputVariant;
    $size: SelectInputSize;
    $isActive: boolean;
    $isDisabled: boolean;
    $isError: boolean;
    $width?: number | string;
    $fillWidth?: boolean;
  }>
>`
  flex: 1;
  outline: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;

  height: ${({ $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => "24px")
      .with(SelectInputSize.MEDIUM, () => "28px")
      .with(SelectInputSize.LARGE, () => "32px")
      .exhaustive();
  }};

  color: ${({ theme }) => theme.color.text.primary};

  font-size: ${({ theme, $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => theme.font.sans.size.body_sm)
      .with(SelectInputSize.MEDIUM, () => theme.font.sans.size.body_md)
      .with(SelectInputSize.LARGE, () => theme.font.sans.size.body_lg)
      .exhaustive();
  }};
  line-height: ${({ theme, $size }) => {
    return match($size)
      .with(SelectInputSize.SMALL, () => theme.font.sans.height.body_sm)
      .with(SelectInputSize.MEDIUM, () => theme.font.sans.height.body_md)
      .with(SelectInputSize.LARGE, () => theme.font.sans.height.body_lg)
      .exhaustive();
  }};
  font-weight: ${({ theme }) => theme.font.sans.weight.medium};
  font-family: ${({ theme }) => theme.font.sans.family};

  &::placeholder {
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  &:disabled {
    cursor: not-allowed;
  }
`);

const MultiSelectInputContent = styled.div<{ $fillWidth?: boolean; $width?: number | string }>`
  display: flex;
  flex-direction: column;

  min-width: ${({ $width }) => getCssSizeValue($width, 200)};
  max-height: 300px;
  width: ${({ $fillWidth, $width }) => {
    if ($width) {
      return getCssSizeValue($width);
    }
    return $fillWidth ? "100%" : "auto";
  }};
`;

const MultiSelectInputOptionsContainer = styled.div<{
  $fillWidth?: boolean;
  $width?: number | string;
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  width: ${({ $fillWidth, $width }) => ($fillWidth || $width ? "100%" : "auto")};
`;

const MultiSelectInputOption = withTheme(styled.div<
  PropsWithTheme<{
    $isFocused?: boolean;
  }>
>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px;

  background: ${({ theme, $isFocused }) =>
    $isFocused ? theme.color.background.tertiary : "transparent"};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }
`);

const PinnedOptionButton = withTheme(styled.button<
  PropsWithTheme<{
    $isFocused?: boolean;
  }>
>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px;

  background: ${({ theme, $isFocused }) =>
    $isFocused ? theme.color.background.tertiary : "transparent"};
  border-radius: 4px;
  border: none;

  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }

  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.color.background.tertiary};
  }
`);

const OptionIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SELECT_INPUT_VARIANT_TO_CHECKBOX_INPUT_VARIANT: Record<
  SelectInputVariant,
  CheckboxInputVariant
> = {
  [SelectInputVariant.BASE]: CheckboxInputVariant.SECONDARY,
  [SelectInputVariant.PRIMARY]: CheckboxInputVariant.SECONDARY,
  [SelectInputVariant.SECONDARY]: CheckboxInputVariant.SECONDARY,
  [SelectInputVariant.TERTIARY]: CheckboxInputVariant.PRIMARY,
};

const MultiSelectInput = ({
  label,
  icon,
  variant = SelectInputVariant.PRIMARY,
  size = SelectInputSize.MEDIUM,
  options,
  value,
  placeholder = "Select options...",
  onChange,
  width,
  fillWidth,
  dropdownWidth,
  dropdownPlacement = DropdownPosition.BOTTOM_START,
  isDisabled = false,
  isRequired = false,
  isOpen: controlledIsOpen,
  isIconTrailing,
  onOpenChange,
  onReset,
  pinnedOptions = [],
  maxDisplayItems = 3,
  renderSelectedText,
  renderSelectedContent,
  error,
  onSearch,
  debounceMs = 300,
}: MultiSelectInputProps) => {
  const isSearchable = !!onSearch;
  const [isHovered, setIsHovered] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const optionsRefs = useRef<(HTMLButtonElement | HTMLDivElement | null)[]>([]);
  const { isOpen, setOpen } = useControlledOpenState({
    isOpen: controlledIsOpen,
    onOpenChange,
  });
  const triggerWidth = useElementWidth(triggerRef);
  const resolvedDropdownWidth = useMemo(
    () => dropdownWidth ?? triggerWidth ?? width,
    [dropdownWidth, triggerWidth, width],
  );

  // Disable the select if explicitly disabled or if there are no options
  const effectiveIsDisabled = isDisabled || options.length === 0;

  const visible = isOpen;
  const debouncedSearchTerm = useDebouncedValue(searchTerm, debounceMs, isSearchable);

  // Auto-focus input when dropdown opens (if searchable)
  useEffect(() => {
    if (visible && isSearchable && inputRef.current) {
      inputRef.current.focus();
      // Select all text when opening for easy replacement
      inputRef.current.select();
    }
  }, [visible, isSearchable]);

  // Clear search when dropdown closes (but keep selected value)
  useEffect(() => {
    if (!visible && isSearchable) {
      setSearchTerm("");
      setFocusedOptionIndex(-1);
    }
  }, [visible, isSearchable]);

  const renderIcon = useCallback(() => {
    if (!icon) {
      return null;
    }

    let iconVariant: IconVariant = SELECT_INPUT_VARIANT_TO_ICON_VARIANT_MAP[variant];
    if (isDisabled) {
      iconVariant = IconVariant.TERTIARY;
    }

    return (
      <FlexItem shrink={0} grow={0}>
        <Icon component={icon} variant={iconVariant} size={SELECT_INPUT_SIZE_TO_ICON_SIZE[size]} />
      </FlexItem>
    );
  }, [icon, variant, size, isDisabled]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!isSearchable || !debouncedSearchTerm.trim()) {
      return options;
    }
    return onSearch(debouncedSearchTerm, options);
  }, [isSearchable, onSearch, debouncedSearchTerm, options]);

  // Hide pinned options when search is active
  const filteredPinnedOptions = useMemo(() => {
    if (!isSearchable || !debouncedSearchTerm.trim()) {
      return pinnedOptions;
    }
    // Hide pinned options when search is active
    return [];
  }, [isSearchable, debouncedSearchTerm, pinnedOptions]);

  const totalOptionsCount = filteredPinnedOptions.length + filteredOptions.length;

  useEffect(() => {
    if (focusedOptionIndex >= totalOptionsCount) {
      setFocusedOptionIndex(-1);
    }
  }, [focusedOptionIndex, totalOptionsCount]);

  useEffect(() => {
    optionsRefs.current = [];
  }, [filteredPinnedOptions, filteredOptions]);

  const handleToggle = useCallback(() => {
    if (effectiveIsDisabled) return;
    setOpen(!visible);
  }, [effectiveIsDisabled, setOpen, visible]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleOptionToggle = (option: SelectInputOption) => {
    if (!onChange) return;

    const isCurrentlySelected = value.some((v) => v.id === option.id);
    let newValue: SelectInputOption[];

    if (isCurrentlySelected) {
      newValue = value.filter((v) => v.id !== option.id);
    } else {
      newValue = [...value, option];
    }

    onChange(newValue);
  };

  const handlePinnedOptionToggle = (pinnedOption: PinnedOptions) => {
    if (!onChange) return;

    const pinnedOptionsData = options.filter((option) =>
      pinnedOption.optionIds.includes(option.id),
    );

    const allPinnedSelected = pinnedOptionsData.every((option) =>
      value.some((v) => v.id === option.id),
    );

    let newValue: SelectInputOption[];

    if (allPinnedSelected) {
      newValue = value.filter((v) => !pinnedOption.optionIds.includes(v.id));
    } else {
      const optionsToAdd = pinnedOptionsData.filter(
        (option) => !value.some((v) => v.id === option.id),
      );
      newValue = [...value, ...optionsToAdd];
    }

    onChange(newValue);
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    }
    handleClose();
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (effectiveIsDisabled) return;

    // If searchable and open, handle keyboard navigation in the input
    if (isSearchable && visible) {
      handleSearchKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
      return;
    }

    // Otherwise, toggle on Enter/Space
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleToggle();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSearchable) return;

    const allOptions = [...filteredPinnedOptions, ...filteredOptions];
    const currentIndex = focusedOptionIndex;
    const focusOptionAtIndex = (nextIndex: number) => {
      setFocusedOptionIndex(nextIndex);
      optionsRefs.current[nextIndex]?.scrollIntoView({ block: "nearest" });
    };

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < allOptions.length - 1) {
          focusOptionAtIndex(currentIndex + 1);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          focusOptionAtIndex(currentIndex - 1);
        } else {
          // If at top, focus back to input
          inputRef.current?.focus();
        }
        break;
      case "Enter":
        e.preventDefault();
        if (currentIndex >= 0 && currentIndex < allOptions.length) {
          if (currentIndex < filteredPinnedOptions.length) {
            // Toggle pinned option
            handlePinnedOptionToggle(filteredPinnedOptions[currentIndex]);
          } else {
            // Toggle regular option
            const optionIndex = currentIndex - filteredPinnedOptions.length;
            handleOptionToggle(filteredOptions[optionIndex]);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
      default:
        // Reset focused index when typing (but allow normal input)
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
          setFocusedOptionIndex(-1);
        }
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setFocusedOptionIndex(-1);
  };

  const isOptionSelected = (optionId: string) => {
    return value.some((v) => v.id === optionId);
  };

  const isPinnedOptionSelected = (pinnedOption: PinnedOptions) => {
    const pinnedOptionsData = options.filter((option) =>
      pinnedOption.optionIds.includes(option.id),
    );
    return pinnedOptionsData.every((option) => value.some((v) => v.id === option.id));
  };

  const isPinnedOptionFocused = (index: number) => {
    return isSearchable && focusedOptionIndex === index && index < filteredPinnedOptions.length;
  };

  const isOptionFocused = (index: number) => {
    return (
      isSearchable && focusedOptionIndex === index + filteredPinnedOptions.length && index >= 0
    );
  };

  const displayText = useMemo(() => {
    if (renderSelectedText) {
      return renderSelectedText(value, placeholder);
    }

    if (value.length === 0) {
      return placeholder;
    }

    if (value.length <= maxDisplayItems) {
      return value.map((v) => v.label).join(", ");
    }

    const displayItems = value.slice(0, maxDisplayItems).map((v) => v.label);
    const remainingCount = value.length - maxDisplayItems;
    return `${displayItems.join(", ")} +${remainingCount} more`;
  }, [value, placeholder, maxDisplayItems, renderSelectedText]);

  const noop = useCallback((_checked: boolean) => {}, []);

  const dropdownBody = (
    <MultiSelectInputContent $fillWidth={fillWidth} $width={resolvedDropdownWidth}>
      {filteredPinnedOptions.length > 0 && (
        <>
          {filteredPinnedOptions.map((pinnedOption, index) => (
            <PinnedOptionButton
              key={pinnedOption.id}
              ref={(el) => {
                optionsRefs.current[index] = el;
              }}
              $isFocused={isPinnedOptionFocused(index)}
              onClick={(e) => {
                e.preventDefault();
                handlePinnedOptionToggle(pinnedOption);
              }}
              onMouseEnter={() => setFocusedOptionIndex(index)}
            >
              <FlexWrapper alignItems={AlignItems.CENTER} gap={8} fillWidth>
                <CheckboxInput
                  variant={SELECT_INPUT_VARIANT_TO_CHECKBOX_INPUT_VARIANT[variant]}
                  isChecked={isPinnedOptionSelected(pinnedOption)}
                  onChange={noop}
                />
                <FlexItem grow={1} shrink={1} minWidth={0}>
                  <Text
                    variant={TextVariant.PRIMARY}
                    size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                    cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
                    isEllipsis
                  >
                    {pinnedOption.label}
                  </Text>
                </FlexItem>
              </FlexWrapper>
            </PinnedOptionButton>
          ))}
          <FlexItem shrink={0}>
            <HorizontalDivider />
          </FlexItem>
        </>
      )}
      <MultiSelectInputOptionsContainer $fillWidth={fillWidth} $width={resolvedDropdownWidth}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const absoluteIndex = filteredPinnedOptions.length + index;
            return (
              <MultiSelectInputOption
                key={option.id}
                ref={(el) => {
                  optionsRefs.current[absoluteIndex] = el;
                }}
                $isFocused={isOptionFocused(index)}
                onClick={(e) => {
                  e.preventDefault();
                  handleOptionToggle(option);
                }}
                onMouseEnter={() => setFocusedOptionIndex(absoluteIndex)}
              >
                <FlexWrapper alignItems={AlignItems.CENTER} gap={8} fillWidth>
                  <CheckboxInput
                    variant={SELECT_INPUT_VARIANT_TO_CHECKBOX_INPUT_VARIANT[variant]}
                    isChecked={isOptionSelected(option.id)}
                    onChange={noop}
                  />
                  {option.icon && <OptionIconSlot>{option.icon}</OptionIconSlot>}
                  <FlexItem grow={1} shrink={1} minWidth={0}>
                    <Text
                      variant={option.variant ?? TextVariant.PRIMARY}
                      size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                      cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
                      isEllipsis
                    >
                      {option.label}
                    </Text>
                  </FlexItem>
                </FlexWrapper>
              </MultiSelectInputOption>
            );
          })
        ) : (
          <div style={{ padding: "8px" }}>
            <Text variant={TextVariant.TERTIARY} size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}>
              No options found
            </Text>
          </div>
        )}
      </MultiSelectInputOptionsContainer>
      {onReset && (
        <>
          <FlexItem shrink={0}>
            <HorizontalDivider />
          </FlexItem>
          <PinnedOptionButton onClick={handleReset} $isFocused={false}>
            <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
              <Icon
                component={ArrowCounterClockwiseIcon}
                variant={IconVariant.TERTIARY}
                size={12}
              />
              <Text
                variant={TextVariant.PRIMARY}
                size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
              >
                Reset
              </Text>
            </FlexWrapper>
          </PinnedOptionButton>
        </>
      )}
    </MultiSelectInputContent>
  );

  // Determine display value: show search term when open and searchable, otherwise show selected value
  const displayValue = useMemo(() => {
    if (isSearchable && visible) {
      return searchTerm;
    }
    return displayText;
  }, [isSearchable, visible, searchTerm, displayText]);

  const textVariant = useMemo(() => {
    if (value.length > 0) {
      return TextVariant.PRIMARY;
    }

    if (!effectiveIsDisabled && (visible || isHovered)) {
      return TextVariant.SECONDARY;
    }

    return TextVariant.TERTIARY;
  }, [value.length, visible, isHovered, effectiveIsDisabled]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={4} fillWidth={fillWidth}>
      {label && <InputLabel label={label} isRequired={isRequired} />}
      <Dropdown
        variant={DropdownVariant.PRIMARY}
        body={dropdownBody}
        isOpen={visible}
        onClose={handleClose}
        minWidth={resolvedDropdownWidth ?? 200}
        offset={[0, 3]}
        fillWidth={fillWidth}
        noPadding
        position={dropdownPlacement}
      >
        {isSearchable && visible ? (
          <StyledMultiSelectInput
            ref={triggerRef}
            $variant={variant}
            $size={size}
            $isActive={visible ?? false}
            $isDisabled={effectiveIsDisabled}
            $isError={!!error}
            $width={width}
            $fillWidth={fillWidth}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleToggle}
          >
            {!isIconTrailing && renderIcon()}
            <StyledSearchInput
              ref={inputRef}
              $variant={variant}
              $size={size}
              $isActive={visible ?? false}
              $isDisabled={effectiveIsDisabled}
              $isError={!!error}
              $width={width}
              $fillWidth={fillWidth}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
              autoCorrect="off"
              disabled={effectiveIsDisabled}
              onClick={(e) => e.stopPropagation()}
            />
            <FlexWrapper>
              <RotateWithTransition isRotated={visible} deg={-180}>
                <Icon
                  component={CaretDownIcon}
                  size={10}
                  variant={
                    !effectiveIsDisabled && (isHovered || visible)
                      ? IconVariant.SECONDARY
                      : IconVariant.TERTIARY
                  }
                />
              </RotateWithTransition>
            </FlexWrapper>
            {isIconTrailing && renderIcon()}
          </StyledMultiSelectInput>
        ) : (
          <StyledMultiSelectInput
            ref={triggerRef}
            $variant={variant}
            $size={size}
            $isActive={visible ?? false}
            $isDisabled={effectiveIsDisabled}
            $isError={!!error}
            $width={width}
            $fillWidth={fillWidth}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="button"
            tabIndex={effectiveIsDisabled ? -1 : 0}
            aria-haspopup="listbox"
            aria-expanded={visible}
            aria-label={displayValue}
          >
            {!isIconTrailing && renderIcon()}
            {renderSelectedContent ? (
              renderSelectedContent(value, placeholder)
            ) : (
              <Text
                variant={textVariant}
                size={SELECT_INPUT_SIZE_TO_TEXT_SIZE_MAP[size]}
                cursor={!effectiveIsDisabled ? "pointer" : "not-allowed"}
                isEllipsis
              >
                {displayValue}
              </Text>
            )}
            <FlexWrapper>
              <RotateWithTransition isRotated={visible} deg={-180}>
                <Icon
                  component={CaretDownIcon}
                  size={10}
                  variant={
                    !effectiveIsDisabled && (isHovered || visible)
                      ? IconVariant.SECONDARY
                      : IconVariant.TERTIARY
                  }
                />
              </RotateWithTransition>
            </FlexWrapper>
            {isIconTrailing && renderIcon()}
          </StyledMultiSelectInput>
        )}
      </Dropdown>
      {error && (
        <Text variant={TextVariant.ERROR} size={TextSize.BODY_SM}>
          {error}
        </Text>
      )}
    </FlexWrapper>
  );
};

export default MultiSelectInput;
